import { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Info, Save, Loader2 } from 'lucide-react';
import { attendanceService } from '@/services/attendance.service';
import { DatePicker } from '@/components/ui/date-picker';
import { TimePicker } from '@/components/ui/time-picker';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useInvalidateAttendance } from '@/hooks/attendance/useInvalidateAttendance';

// ── Props ──────────────────────────────────────────────────────────────────────

interface Props {
  userId:    string;
  userName:  string;
  date:      string;
  onClose:   () => void;
  onSuccess: () => void;
}

// Suggests punch type from a 24h "HH:MM" time string per the shift-window convention:
// 6–12 check-in, 12–13 check-out, 13–15 check-in, 15–24 & 0–6 check-out.
function suggestPunchType(hhmm: string): 'check_in' | 'check_out' {
  const hour = Number(hhmm.split(':')[0]);
  if (hour >= 6 && hour < 12) return 'check_in';
  if (hour >= 12 && hour < 13) return 'check_out';
  if (hour >= 13 && hour < 15) return 'check_in';
  return 'check_out'; // 15:00–23:59 and 00:00–05:59
}

// ── Component ──────────────────────────────────────────────────────────────────

export function AddPunchModal({ userId, userName, date, onClose, onSuccess }: Props) {
  const [punchType,  setPunchType]  = useState<'check_in' | 'check_out'>('check_in');
  const [punchDate,  setPunchDate]  = useState(date);
  const [punchTime,  setPunchTime]  = useState('');
  const [workMode,   setWorkMode]   = useState('office');
  const [reason,     setReason]     = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const invalidateAttendance = useInvalidateAttendance();

  function handleTimeChange(value: string) {
    setPunchTime(value);
    setPunchType(suggestPunchType(value));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!punchTime || !reason.trim()) {
      setError('Time and Reason are required.');
      return;
    }

    setError(null);
    setSubmitting(true);

    try {
      // Parse IST time and convert to UTC ISO string (Zod datetime() requires Z suffix)
      const punchIso = new Date(`${punchDate}T${punchTime}:00+05:30`).toISOString();

      await attendanceService.addAdminPunch({
        user_id:   userId,
        punch_type: punchType,
        punch_time: punchIso,
        work_mode:  workMode,
        metadata:   { reason: reason.trim(), added_by: 'admin' },
      });

      invalidateAttendance();
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to add punch';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return createPortal(
    <div
      className="fixed inset-0 z-modal-nested flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }}
    >
      <div className="relative w-full max-w-md rounded-2xl border border-slate-200 dark:border-border bg-white dark:bg-card shadow-2xl">

        {/* Header */}
        <div className="flex items-center gap-3 border-b border-slate-100 dark:border-border/50 px-5 py-4">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-orange-50 dark:bg-orange-900/20 border border-orange-100 dark:border-orange-900/40">
            <Plus className="h-4.5 w-4.5 text-orange-500" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-bold text-slate-800 dark:text-slate-200">Add Missing Punch</h2>
            <p className="text-xs text-slate-400 dark:text-slate-500 truncate">{userName}</p>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-muted dark:hover:text-slate-300 transition-colors disabled:opacity-40"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Info note */}
        <div className="mx-5 mt-4 flex items-start gap-2.5 rounded-lg border border-teal-200 bg-teal-50 px-3.5 py-2.5 text-xs text-teal-700">
          <Info className="h-4 w-4 shrink-0 mt-0.5 text-teal-500" />
          <span>For Check In / Out, punches will be auto-resequenced during daily record computation.</span>
        </div>

        {/* Form */}
        <form ref={formRef} onSubmit={handleSubmit} className="px-5 py-4 space-y-4">

          {/* Punch Type */}
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">
              Punch Type
            </label>
            <div className="flex overflow-hidden rounded-lg border border-slate-200 text-sm">
              {(['check_in', 'check_out'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setPunchType(t)}
                  className={`flex-1 py-2 font-medium transition-colors ${
                    punchType === t
                      ? t === 'check_in'
                        ? 'bg-emerald-600 text-white'
                        : 'bg-rose-500 text-white'
                      : 'bg-white dark:bg-muted text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-muted'
                  }`}
                >
                  {t === 'check_in' ? 'Check In' : 'Check Out'}
                </button>
              ))}
            </div>
          </div>

          {/* Date + Time row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">
                Date
              </label>
              <DatePicker value={punchDate} onChange={setPunchDate} />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">
                Time <span className="text-rose-400">*</span>
              </label>
              <TimePicker value={punchTime} onChange={handleTimeChange} />
            </div>
          </div>

          {/* Work Mode */}
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Work Mode
            </label>
            <Select value={workMode} onValueChange={setWorkMode}>
              <SelectTrigger className="rounded-lg border-slate-200 dark:border-border bg-white dark:bg-muted text-slate-800 dark:text-slate-200 focus:border-teal-400 focus:ring-teal-300">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="office">Office</SelectItem>
                <SelectItem value="remote">Remote</SelectItem>
                <SelectItem value="hybrid">Hybrid</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Reason */}
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">
              Reason <span className="text-rose-400">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              rows={3}
              placeholder="Reason for manual punch correction…"
              className="w-full resize-none rounded-lg border border-slate-200 dark:border-border bg-white dark:bg-muted px-3 py-2 text-sm text-slate-800 dark:text-slate-200 placeholder-slate-300 dark:placeholder-slate-600 focus:border-teal-400 focus:outline-none focus:ring-1 focus:ring-teal-300"
            />
          </div>

          {/* Error */}
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="rounded-lg border border-slate-200 dark:border-border bg-white dark:bg-muted px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-muted transition-colors disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex items-center gap-2 rounded-lg bg-teal-600 px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-700 transition-colors disabled:opacity-60"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {submitting ? 'Saving…' : 'Add Punch'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
